import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { apiService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import EvaluationSummary, { ReportDisclosure } from '../../components/Evaluation/EvaluationSummary';
import { evaluationCopy } from '../../utils/evaluationCopy';
import { getInstructorReview } from '../../utils/evaluationPresentation';
import { printEvaluationReport } from '../../utils/printEvaluation';
import './EvaluationResults.css';

function roleLabel(role, t) {
  if (role === 'user') return t('youStudent');
  if (role === 'patient') return t('patientProfile');
  return t('clinicalSystem');
}

export default function EvaluationResults() {
  const { language, t } = useLanguage();
  const { user, token } = useAuth();
  const { submissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [retry, setRetry] = useState(0);
  const reportRef = useRef(null);
  const printCleanup = useRef(null);
  const userId = user?.id;
  const requestIdentity = useMemo(() => ({ userId, token, submissionId, locationKey: location.key, retry }), [userId, token, submissionId, location.key, retry]);
  // Navigation state can contain an old grade or a different account's work.
  const currentResult = result?.identity === requestIdentity ? result : null;
  const submission = currentResult?.submission;
  const loading = !currentResult;
  const error = currentResult?.error;
  const copy = evaluationCopy(language);

  useEffect(() => {
    const controller = new AbortController();
    if (!requestIdentity.userId || !requestIdentity.token || !requestIdentity.submissionId) {
      setResult({ identity: requestIdentity, error: 'notFound' });
      return () => controller.abort();
    }
    apiService.getMySubmissions({ signal: controller.signal })
      .then((items) => {
        if (controller.signal.aborted) return;
        const found = (Array.isArray(items) ? items : []).find((item) => (
          String(item.id) === String(requestIdentity.submissionId) &&
          (item.user_id == null || String(item.user_id) === String(requestIdentity.userId))
        ));
        setResult({ identity: requestIdentity, submission: found, error: found ? null : 'notFound' });
      })
      .catch(() => {
        if (!controller.signal.aborted) setResult({ identity: requestIdentity, error: 'loadFailed' });
      });
    return () => controller.abort();
  }, [requestIdentity]);

  useEffect(() => () => printCleanup.current?.(), []);
  const printReport = mode => {
    printCleanup.current?.();
    printCleanup.current = printEvaluationReport(reportRef.current, mode);
  };
  const transcript = Array.isArray(submission?.transcript) ? submission.transcript : [];

  if (loading) return <div className="results-state">{t('evaluationLoading')}</div>;
  if (error || !submission) {
    return <div className="results-state results-error"><p role="alert">{error === 'loadFailed' ? copy.loadFailed : t('evaluationNotFound')}</p><button type="button" onClick={() => setRetry(value => value + 1)}>{copy.retry}</button><button type="button" onClick={() => navigate('/dashboard')}>{t('backToCases')}</button></div>;
  }

  return (
    <main className="results-page" ref={reportRef} data-print-mode="summary">
      <div className="results-shell">
        <header className="results-topbar">
          <button type="button" className="results-back" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft /> {t('backToCases')}
          </button>
          <span className="results-case">{submission.patient_name || submission.case_id}</span>
        </header>
        <div className="results-heading">
          <span>{t('encounterCompleted')}</span>
          <h1>{t('clinicalEvaluation')}</h1>
        </div>

        <EvaluationSummary evaluation={submission.system_result} fallbackScore={submission.system_score} instructorReview={getInstructorReview(submission)} />

        <ReportDisclosure title={copy.submittedAnswers}>
          <section className="results-answer-grid">
            <article><h2>{t('yourDiagnosis')}</h2><p>{submission.student_diagnosis || '—'}</p></article>
            <article><h2>{t('managementPlan')}</h2><p>{submission.student_plan || '—'}</p></article>
          </section>
        </ReportDisclosure>

        <ReportDisclosure title={<>{t('fullTranscript')} <span className="results-message-count">({transcript.length} {t('messages')})</span></>}>
          <section aria-label={t('fullTranscript')}>
            {transcript.length ? <div className="results-messages">
              {transcript.map((message, index) => <div className={'results-message results-message-' + message.role} key={message.role + '-' + index}>
                <span>{roleLabel(message.role, t)}</span>
                <p>{message.content}</p>
              </div>)}
            </div> : <p className="results-empty">{t('noTranscript')}</p>}
          </section>
        </ReportDisclosure>

        <div className="results-actions">
          <button type="button" className="results-primary" onClick={() => navigate('/dashboard')}>{t('chooseAnother')}</button>
          <button type="button" className="results-secondary" onClick={() => printReport('summary')}>{copy.printSummary}</button>
          <button type="button" className="results-secondary" onClick={() => printReport('full')}>{copy.printFullReport}</button>
        </div>
      </div>
    </main>
  );
}
